import { useMutation, useQueryClient } from 'react-query';

import type { CreatePasswordDto } from '@identity/application/dtos';
import { getAccountUseCases } from '@identity/composition';
import { useIdentityFlow } from '@identity/presentation/context';
import { useIdentityUserId } from '@identity/presentation/hooks/useIdentityUserId';
import { patchSatisfiedRequirement } from '@identity/presentation/utils';

/**
 * Saves the password and lets the wizard advance without waiting for a refetch.
 *
 * The cache patch runs after the write succeeds, so a rejected password leaves the map unchanged and
 * the user stays on this step. That is why it is not an optimistic mutation.
 */
export const useCreatePassword = () => {
  const queryClient = useQueryClient();
  const flow = useIdentityFlow();
  const userId = useIdentityUserId();
  const { createPassword } = getAccountUseCases(flow);

  return useMutation<void, Error, CreatePasswordDto>((input) => createPassword.execute(input), {
    onSuccess: () => {
      patchSatisfiedRequirement(queryClient, 'PASSWORD', userId);
    },
  });
};
